import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  Box,
  Divider,
  IconButton,
  Collapse,
  Link,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AppleIcon from '@mui/icons-material/Apple';
import AndroidIcon from '@mui/icons-material/Android';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';
import SettingsBrightnessIcon from '@mui/icons-material/SettingsBrightness';
import { useSettingsStore, type ThemeMode } from '../../stores/settingsStore';
import {
  ABOUT_URL,
  PRIVACY_POLICY_URL,
  APP_STORE_URL,
  PLAY_STORE_URL,
} from '../../utils/appLinks';

interface SettingsModalProps {
  open: boolean;
  onClose: () => void;
}

interface SettingItemProps {
  title: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  description: string;
  expandedId: string;
  currentExpanded: string | null;
  onToggleExpand: (id: string) => void;
}

const SettingItem = ({
  title,
  checked,
  onChange,
  description,
  expandedId,
  currentExpanded,
  onToggleExpand,
}: SettingItemProps) => {
  const isExpanded = currentExpanded === expandedId;

  return (
    <Box sx={{ mb: 1 }}>
      <Box sx={{ display: 'flex', alignItems: 'center' }}>
        <FormControlLabel
          control={
            <Switch
              checked={checked}
              onChange={(e) => onChange(e.target.checked)}
            />
          }
          label={title}
        />
        <IconButton
          onClick={() => onToggleExpand(expandedId)}
          size="small"
          sx={{
            ml: 'auto',
            p: 1,
            color: 'primary.main',
          }}
        >
          {isExpanded ? (
            <ExpandLessIcon sx={{ fontSize: 22 }} />
          ) : (
            <InfoOutlinedIcon sx={{ fontSize: 22 }} />
          )}
        </IconButton>
      </Box>
      <Collapse in={isExpanded} timeout={200}>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ ml: 4, mt: 0.5, mb: 1 }}
        >
          {description}
        </Typography>
      </Collapse>
    </Box>
  );
};

const SettingsModal = ({ open, onClose }: SettingsModalProps) => {
  const [expandedSetting, setExpandedSetting] = useState<string | null>(null);

  const {
    continueJamsMode,
    fullShowMode,
    shouldLoadPreviousBands,
    shouldLoadPreviousSettings,
    skipFillerSongs,
    shouldShowGreat78Artists,
    themeMode,
    updateThemeMode,
    updateContinueJamsMode,
    updateFullShowMode,
    updateSkipFillerSongs,
    updateShouldLoadPreviousBands,
    updateShouldLoadPreviousSettings,
    updateShouldShowGreat78Artists,
  } = useSettingsStore();

  const handleToggleExpand = (id: string) => {
    setExpandedSetting(expandedSetting === id ? null : id);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h4" component="div" sx={{ fontWeight: 700 }}>
          Settings
        </Typography>
      </DialogTitle>

      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Appearance
            </Typography>

            <ToggleButtonGroup
              value={themeMode}
              exclusive
              onChange={(_, value: ThemeMode | null) => {
                if (value) updateThemeMode(value);
              }}
              size="small"
              aria-label="Theme mode"
            >
              <ToggleButton value="system" aria-label="Follow system theme">
                <SettingsBrightnessIcon sx={{ fontSize: 18, mr: 0.75 }} />
                System
              </ToggleButton>
              <ToggleButton value="light" aria-label="Light theme">
                <LightModeIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Light
              </ToggleButton>
              <ToggleButton value="dark" aria-label="Dark theme">
                <DarkModeIcon sx={{ fontSize: 18, mr: 0.75 }} />
                Dark
              </ToggleButton>
            </ToggleButtonGroup>
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Playback Settings
            </Typography>

            <SettingItem
              title="Continue Jams Mode"
              checked={continueJamsMode}
              onChange={updateContinueJamsMode}
              description="When enabled, any song that ends with > will automatically load the next song(s) in the jam sequence"
              expandedId="continueJams"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />

            <SettingItem
              title="Full Show Mode"
              checked={fullShowMode}
              onChange={updateFullShowMode}
              description="When enabled, full shows are loaded at a time"
              expandedId="fullShow"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />

            <SettingItem
              title="Skip Filler Songs"
              checked={skipFillerSongs}
              onChange={updateSkipFillerSongs}
              description="When enabled, short tracks (≤180s) with titles like tuning, crowd, banter, or announcements will be skipped. Does not apply in Full Show mode"
              expandedId="skipFiller"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Startup Settings
            </Typography>

            <SettingItem
              title="Load Previous Bands"
              checked={shouldLoadPreviousBands}
              onChange={updateShouldLoadPreviousBands}
              description="When enabled, previously selected bands will be loaded on app start"
              expandedId="loadBands"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />

            <SettingItem
              title="Load Previous Settings"
              checked={shouldLoadPreviousSettings}
              onChange={updateShouldLoadPreviousSettings}
              description="When enabled, previous session settings will be loaded on app start"
              expandedId="loadSettings"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Content Settings
            </Typography>

            <SettingItem
              title="Show Great 78 Artists"
              checked={shouldShowGreat78Artists}
              onChange={updateShouldShowGreat78Artists}
              description="When enabled, artists from the Great 78 Shuffle app will be shown"
              expandedId="great78"
              currentExpanded={expandedSetting}
              onToggleExpand={handleToggleExpand}
            />
          </Box>

          <Divider />

          <Box>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              About
            </Typography>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mb: 2 }}>
              <Link
                href={ABOUT_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                About Live Shuffle
              </Link>
              <Link
                href={PRIVACY_POLICY_URL}
                target="_blank"
                rel="noopener noreferrer"
                underline="hover"
              >
                Privacy Policy
              </Link>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              Get the mobile app:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <Button
                variant="outlined"
                startIcon={<AppleIcon />}
                href={APP_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                App Store
              </Button>
              <Button
                variant="outlined"
                startIcon={<AndroidIcon />}
                href={PLAY_STORE_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                Google Play
              </Button>
            </Box>
          </Box>
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} variant="contained">
          Done
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SettingsModal;
